/**
 * RFQ request coverage — "what has been requested already, and what is new since".
 *
 * Pure: proposal structures + timber-list tracks + request rounds (+ legacy packages
 * published from the Suppliers tab) → one lane per structure scope with the current
 * timber-list head versus the last version suppliers were asked to quote.
 *
 * A lane is the set of structures one timber list covers. A list tagged to a single
 * structure is its own lane; a list tagged "Barn + Shop" is one lane for both; a
 * project with no structure-tagged timber lists has a single Whole project lane.
 */

import type {
  ProjectFile,
  ProjectFileStream,
  RfqHubFileRelease,
  RfqPackage,
  RfqRequest,
  RfqRequestLine,
  RfqSubmission,
  TimberSupplierStructureRef,
} from '../types';
import { headVersionForStream, versionsForStream } from './projectFileStreamVersions';
import type { RfqProjectApprovalFields, RfqSupplierStatus } from './projectRfqFiles';
import type { StructureRef } from './proposalStructureRefs';
import { unsentStagedReleases } from './rfqReleaseGroups';
import { structureSelectionLabel, structureSetKey, WHOLE_PROJECT_SELECTION_LABEL } from './structureSelectionLabel';

export const WHOLE_PROJECT_LANE_KEY = structureSetKey([]);

export type RfqCoverageState =
  /** No timber list exists for these structures yet. */
  | 'no_list'
  /** A list exists but suppliers were never asked to quote it. */
  | 'not_requested'
  /** A list was staged for suppliers (Suppliers tab) but no request has gone out. */
  | 'staged'
  /** The current head is the version suppliers have. */
  | 'requested'
  /** A newer list was uploaded after the last request. */
  | 'newer_list';

export interface RfqLaneSupplierState {
  supplierOrgId: string;
  packageId: string | null;
  status: RfqSupplierStatus;
}

export interface RfqLaneLastRequest {
  /** `null` when the last send came from the Suppliers tab (no request envelope). */
  request: RfqRequest | null;
  line: RfqRequestLine | null;
  sentAt: Date;
  versionIndex?: number;
  versionLabel?: string;
  sourceFileId?: string;
  suppliers: RfqLaneSupplierState[];
}

export interface RfqCoverageLane {
  /** {@link structureSetKey} of the lane's structures. */
  key: string;
  structureIds: string[];
  structureNames: string[];
  label: string;
  /** Timber-list track for this scope; `null` when none has been uploaded. */
  stream: ProjectFileStream | null;
  /** Current head version of that track. */
  head: ProjectFile | null;
  lastRequest: RfqLaneLastRequest | null;
  /** Newest staged release on this track that no supplier has been sent (any state). */
  staged: RfqHubFileRelease | null;
  state: RfqCoverageState;
}

export interface BuildRfqCoverageInput {
  structures: ReadonlyArray<StructureRef>;
  streams: ReadonlyArray<ProjectFileStream>;
  files: ReadonlyArray<ProjectFile>;
  requests: ReadonlyArray<RfqRequest>;
  /** Legacy sends (Suppliers tab) are read from packages + the staged releases behind them. */
  packages?: ReadonlyArray<RfqPackage>;
  hubReleases?: ReadonlyArray<RfqHubFileRelease>;
  submissions?: ReadonlyArray<RfqSubmission>;
  projectById?: ReadonlyMap<string, RfqProjectApprovalFields>;
}

const isTimberList = (s: ProjectFileStream) => s.streamKind === 'timber_list_csv';

/** Timber-list tracks staff can request quotes from: not a split root, with at least one version. */
export function requestableTimberListTracks(
  streams: ReadonlyArray<ProjectFileStream>,
  files: ReadonlyArray<ProjectFile>,
): ProjectFileStream[] {
  return streams.filter(s => s.id && isTimberList(s) && !s.wholeProjectRoot && versionsForStream(s, files as ProjectFile[]).length > 0);
}

function newest<T>(items: ReadonlyArray<T>, at: (t: T) => Date | undefined): T | null {
  let best: T | null = null;
  let bestT = -Infinity;
  for (const it of items) {
    const d = at(it);
    const t = d instanceof Date ? d.getTime() : 0;
    if (t > bestT) {
      best = it;
      bestT = t;
    }
  }
  return best;
}

/**
 * One supplier's standing on one package: Approved beats Quoted beats Not bidding
 * beats Sent. Approval is read from the work order's per-release / global lists.
 */
export function packageSupplierStatus(
  pkg: Pick<RfqPackage, 'id' | 'rfqProjectId' | 'hubFileReleaseId' | 'supplierOrgId'> | null | undefined,
  submissions: ReadonlyArray<RfqSubmission>,
  projectById?: ReadonlyMap<string, RfqProjectApprovalFields>,
): RfqSupplierStatus {
  if (!pkg?.id) return 'sent';
  const subs = submissions.filter(s => s.rfqPackageId === pkg.id);
  const quotes = subs.filter(s => !s.notBidding && (!!s.storagePath || !!s.cncQuote));
  if (quotes.length) {
    const project = projectById?.get(pkg.rfqProjectId);
    const approved = new Set<string>([
      ...(project?.approvedSubmissionIds ?? []),
      ...(pkg.hubFileReleaseId ? project?.approvedSubmissionIdsByRelease?.[pkg.hubFileReleaseId] ?? [] : []),
      ...(project?.approvedSubmissionIdsByRelease?.[pkg.id] ?? []),
    ]);
    if (quotes.some(q => q.id && approved.has(q.id))) return 'approved';
    return 'quoted';
  }
  if (subs.some(s => s.notBidding)) return 'not_bidding';
  return 'sent';
}

/** Supplier × status for one request line. */
export function requestLineSupplierStates(
  request: Pick<RfqRequest, 'supplierOrgIds'>,
  line: Pick<RfqRequestLine, 'packageIdByOrg'>,
  packages: ReadonlyArray<RfqPackage>,
  submissions: ReadonlyArray<RfqSubmission>,
  projectById?: ReadonlyMap<string, RfqProjectApprovalFields>,
): RfqLaneSupplierState[] {
  return request.supplierOrgIds.map(supplierOrgId => {
    const packageId = line.packageIdByOrg?.[supplierOrgId] ?? null;
    const pkg = packageId ? packages.find(p => p.id === packageId) : undefined;
    return {
      supplierOrgId,
      packageId,
      status: packageSupplierStatus(pkg ?? (packageId ? { id: packageId, rfqProjectId: '', supplierOrgId } : null), submissions, projectById),
    };
  });
}

/** A round is open while any supplier on any line has only "Sent". */
export function isRequestOpen(
  request: RfqRequest,
  packages: ReadonlyArray<RfqPackage>,
  submissions: ReadonlyArray<RfqSubmission>,
  projectById?: ReadonlyMap<string, RfqProjectApprovalFields>,
): boolean {
  return request.lines.some(line =>
    requestLineSupplierStates(request, line, packages, submissions, projectById).some(s => s.status === 'sent'),
  );
}

export function countOpenRequests(
  requests: ReadonlyArray<RfqRequest>,
  packages: ReadonlyArray<RfqPackage>,
  submissions: ReadonlyArray<RfqSubmission>,
  projectById?: ReadonlyMap<string, RfqProjectApprovalFields>,
): number {
  return requests.filter(r => isRequestOpen(r, packages, submissions, projectById)).length;
}

/** Lane key for a line / package scope (structures → set key; empty = whole project). */
export function laneKeyForStructures(structures: ReadonlyArray<TimberSupplierStructureRef | StructureRef> | undefined): string {
  return structureSetKey((structures ?? []).map(s => s?.id ?? ''));
}

/**
 * Build the coverage lanes. Lane order follows the proposal (first structure of each
 * lane), structures without a list after, Whole project last.
 */
export function buildRfqCoverage(input: BuildRfqCoverageInput): RfqCoverageLane[] {
  const files = input.files as ProjectFile[];
  const packages = input.packages ?? [];
  const hubReleases = input.hubReleases ?? [];
  const submissions = input.submissions ?? [];
  const nameOf = new Map<string, string>(input.structures.map(s => [s.id, s.name]));
  for (const s of input.streams) {
    (s.structureIds ?? []).forEach((id, i) => {
      if (!nameOf.has(id) && s.structureNames?.[i]) nameOf.set(id, s.structureNames[i]);
    });
  }
  const allIds = input.structures.map(s => s.id);
  const coversWhole = (ids: ReadonlyArray<string>) => allIds.length > 0 && allIds.every(id => ids.includes(id));
  const normalizeIds = (ids: ReadonlyArray<string>) => (coversWhole(ids) ? [] : Array.from(new Set(ids.filter(Boolean))).sort());

  // ── Lanes from timber-list tracks ──
  const tracks = requestableTimberListTracks(input.streams, files);
  const laneStream = new Map<string, ProjectFileStream>();
  const laneIds = new Map<string, string[]>();
  for (const track of tracks) {
    const ids = normalizeIds(track.structureIds ?? []);
    const key = structureSetKey(ids);
    const prev = laneStream.get(key);
    // Two lists for the same scope: keep the one updated most recently.
    if (!prev || (track.updatedAt?.getTime?.() ?? 0) > (prev.updatedAt?.getTime?.() ?? 0)) laneStream.set(key, track);
    laneIds.set(key, ids);
  }
  // Structures no list covers get an empty lane so staff can see the gap — unless a
  // whole-project list exists, in which case that list covers them.
  const hasWholeLane = laneStream.has(WHOLE_PROJECT_LANE_KEY);
  const covered = new Set(Array.from(laneIds.values()).flat());
  if (!hasWholeLane) {
    for (const st of input.structures) {
      if (covered.has(st.id)) continue;
      const key = structureSetKey([st.id]);
      if (!laneIds.has(key)) laneIds.set(key, [st.id]);
    }
  }
  if (laneIds.size === 0) laneIds.set(WHOLE_PROJECT_LANE_KEY, []);

  // ── Last request per lane ──
  const releaseById = new Map<string, RfqHubFileRelease>();
  for (const r of hubReleases) if (r.id) releaseById.set(r.id, r);
  const requestPackageIds = new Set<string>();
  for (const r of input.requests) for (const l of r.lines) for (const id of Object.values(l.packageIdByOrg ?? {})) requestPackageIds.add(id);

  const lastRequestFor = (key: string, stream: ProjectFileStream | null): RfqLaneLastRequest | null => {
    const candidates: RfqLaneLastRequest[] = [];
    for (const req of input.requests) {
      for (const line of req.lines) {
        const lineKey = structureSetKey(normalizeIds(line.structures.map(s => s.id)));
        const sameTrack = !!stream?.id && line.sourceStreamId === stream.id;
        if (lineKey !== key && !sameTrack) continue;
        candidates.push({
          request: req,
          line,
          sentAt: req.sentAt,
          versionIndex: line.versionIndex,
          versionLabel: line.versionLabel,
          sourceFileId: line.sourceFileId,
          suppliers: requestLineSupplierStates(req, line, packages, submissions, input.projectById),
        });
      }
    }
    // Legacy: packages published from the Suppliers tab for this lane's scope / track.
    const legacy = packages.filter(p => {
      if (!p.id || requestPackageIds.has(p.id) || p.rfqRequestId) return false;
      if (p.workType && p.workType !== 'timber_rfq') return false;
      const rel = p.hubFileReleaseId ? releaseById.get(p.hubFileReleaseId) : undefined;
      if (stream?.id && rel?.sourceStreamId === stream.id) return true;
      const scope = (p.scopeStructures ?? []).map(s => s.id);
      if (scope.length) return structureSetKey(normalizeIds(scope)) === key;
      if (rel?.structureIds?.length) return structureSetKey(normalizeIds(rel.structureIds)) === key;
      return key === WHOLE_PROJECT_LANE_KEY && !!rel;
    });
    if (legacy.length) {
      // Group by release so one send to three suppliers is one entry.
      const byRelease = new Map<string, RfqPackage[]>();
      for (const p of legacy) {
        const k = p.hubFileReleaseId || p.id!;
        byRelease.set(k, [...(byRelease.get(k) ?? []), p]);
      }
      byRelease.forEach(pkgs => {
        const rel = pkgs[0].hubFileReleaseId ? releaseById.get(pkgs[0].hubFileReleaseId) : undefined;
        candidates.push({
          request: null,
          line: null,
          sentAt: newest(pkgs, p => p.createdAt)?.createdAt ?? new Date(0),
          versionIndex: rel?.versionIndex,
          versionLabel: rel?.versionLabel,
          sourceFileId: rel?.sourceFileId,
          suppliers: pkgs.map(p => ({
            supplierOrgId: p.supplierOrgId,
            packageId: p.id ?? null,
            status: packageSupplierStatus(p, submissions, input.projectById),
          })),
        });
      });
    }
    return newest(candidates, c => c.sentAt);
  };

  // ── Staged but never sent (Suppliers tab staging without a round) ──
  const unsent = unsentStagedReleases({ releases: hubReleases.filter(r => r.workType === 'timber_rfq'), packages, requests: input.requests });
  const stagedFor = (stream: ProjectFileStream | null): RfqHubFileRelease | null => {
    if (!stream?.id) return null;
    const versionIds = new Set(versionsForStream(stream, files).map(v => v.id));
    return (
      unsent
        .filter(r => r.sourceStreamId === stream.id && versionIds.has(r.sourceFileId))
        .sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex)[0] ?? null
    );
  };

  const lanes: RfqCoverageLane[] = [];
  laneIds.forEach((ids, key) => {
    const stream = laneStream.get(key) ?? null;
    const head = stream ? headVersionForStream(stream, files) ?? null : null;
    const last = lastRequestFor(key, stream);
    const staged = stagedFor(stream);
    let state: RfqCoverageState;
    if (!stream || !head) state = last ? 'requested' : 'no_list';
    else if (!last) state = staged ? 'staged' : 'not_requested';
    else if (last.sourceFileId && head.id && last.sourceFileId === head.id) state = 'requested';
    else if (typeof last.versionIndex === 'number' && typeof head.versionIndex === 'number') {
      state = head.versionIndex > last.versionIndex ? 'newer_list' : 'requested';
    } else state = last.sentAt.getTime() < (head.uploadedAt?.getTime?.() ?? 0) ? 'newer_list' : 'requested';
    const names = ids.map(id => nameOf.get(id) || 'Structure');
    lanes.push({
      key,
      structureIds: ids,
      structureNames: names,
      label: key === WHOLE_PROJECT_LANE_KEY ? WHOLE_PROJECT_SELECTION_LABEL : structureSelectionLabel(names, { maxNamed: 3 }),
      stream,
      head,
      lastRequest: last,
      staged,
      state,
    });
  });

  const pos = new Map<string, number>(allIds.map((id, i) => [id, i]));
  lanes.sort((a, b) => {
    if (a.key === WHOLE_PROJECT_LANE_KEY) return 1;
    if (b.key === WHOLE_PROJECT_LANE_KEY) return -1;
    const pa = Math.min(...a.structureIds.map(id => pos.get(id) ?? Number.MAX_SAFE_INTEGER));
    const pb = Math.min(...b.structureIds.map(id => pos.get(id) ?? Number.MAX_SAFE_INTEGER));
    return pa !== pb ? pa - pb : a.label.localeCompare(b.label);
  });
  return lanes;
}

/** Short chip copy: "Not requested" / "Staged #3 · not sent" / "Requested #2 · 3 suppliers · 2 quoted" / "Newer list #3 since last request #2". */
export function coverageLaneSummary(lane: RfqCoverageLane): string {
  const v = (idx: number | undefined) => (typeof idx === 'number' ? `#${idx}` : '');
  const last = lane.lastRequest;
  const counts = () => {
    if (!last) return '';
    const n = last.suppliers.length;
    const quoted = last.suppliers.filter(s => s.status === 'quoted' || s.status === 'approved').length;
    const parts = [`${n} supplier${n !== 1 ? 's' : ''}`];
    if (quoted) parts.push(`${quoted} quoted`);
    return parts.join(' · ');
  };
  switch (lane.state) {
    case 'no_list':
      return 'No timber list yet';
    case 'not_requested':
      return 'Not requested';
    case 'staged':
      return `Staged ${v(lane.staged?.versionIndex ?? lane.head?.versionIndex)} · not sent`.replace(/\s+/g, ' ').trim();
    case 'requested':
      return `Requested ${v(last?.versionIndex)}`.trim() + (counts() ? ` · ${counts()}` : '');
    case 'newer_list':
      return `Newer list ${v(lane.head?.versionIndex)} since last request ${v(last?.versionIndex)}`.replace(/\s+/g, ' ').trim();
    default:
      return '';
  }
}
